import CloseIcon from '@mui/icons-material/Close';
import FormActionButtons from '../components/FormActionButtons';
import EditIcon from '@mui/icons-material/BorderColorOutlined';
import { Box, Grid, IconButton, Link, Tooltip, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import moment from 'moment-timezone';
import Image from 'mui-image';
import { useSnackbar } from 'notistack';
import * as React from 'react';
import { useHistory } from 'react-router-dom';
import { rrulestr } from 'rrule';

import { DataAPIContext, DELETE } from '../api';
import AccessDeniedView from '../components/AccessDeniedView';
import ConfirmationDialog from '../components/ConfirmationDialog';
import images from '../components/images';
import { RecordDetails, RecordDetailsContext } from '../components/RecordDetails';
import { ContextStore } from '../store/contextStore';
import { disableMuiButton, enableMuiButton } from '../utils';
import { ConfirmationDialogText } from './utils';

const useStyles = makeStyles((theme) => ({
    root: {
        padding: theme.spacing(2),
        [theme.breakpoints.down('sm')]: {
            margin: 0,
            padding: 0,
        },
    },
    scrollableRoot: {
        height: `calc(${theme.cp.contentHeight} - 4.75rem)`,
        marginTop: '-2rem',
        overflowY: 'scroll',
        [theme.breakpoints.down('sm')]: {
            height: `calc(${theme.cp.contentHeight} - 5rem)`,
        },
    },
    field: {
        padding: theme.spacing(2),
    },
    label: {
        color: '#747474',
        fontSize: '12px',
        lineHeight: '16px',
    },
    value: {
        color: '#202020',
        fontSize: '16px',
        lineHeight: '24px',
    },
    subvalue: {
        color: '#aaaaaa',
        fontSize: '16px',
    },
    previewImage: {
        borderRadius: '10px',
    },

}));

const EMDASH = String.fromCharCode(8212);

const PreviewField = (props) => {
    const classes = useStyles();
    return (
        <div className={classes.field}>
            <div className={classes.label}>{props.label}</div>
            <div className={classes.value}>
                <Typography variant="body2">{props.value || EMDASH}</Typography>
            </div>
            {props.subvalue ? (
                <div className={classes.subvalue}>
                    <Typography variant="body2">{props.subvalue}</Typography>
                </div>
            ) : null}
        </div>
    );
};

const CPImage = (props) => {
    console.log('Image', props);
    const nximg = props.placeholder || <div className="non-existing-image-placeholder" />;
    const { settings } = React.useContext(ContextStore);

    if (!props.src) {
        return nximg;
    }
    const fit = props.fit || 'cover';
    const preset = `${fit}_${props.width}x${props.height}`;
    const imageURL = images.url(settings.images.base, props.src, preset);
    console.log('Image', imageURL);
    if (!imageURL) {
        return nximg;
    }
    const imageStyle = {};
    if (props.roundCorners) {
        imageStyle.borderRadius = `${props.roundCorners}px`;
    }
    return <Image src={imageURL} cover={true} aspectRatio={props.width / props.height} height={props.height} style={imageStyle} />;
};

const PreviewImage = (props) => {
    const classes = useStyles();
    const img = props.image; // list[props.idx || 0];
    if (!img) {
        return <div />;
    }
    console.log('PreviewImage', props, img);
    return <CPImage src={img} fit="cover" width={368} height={250} className={props.className || classes.previewImage} roundCorners={10} />;
};

const TripPreviewPanel = (props) => {
    const dataAPI = React.useContext(DataAPIContext);
    const history = useHistory();
    const classes = useStyles();
    const deleteRef = React.useRef(null);
    const editRef = React.useRef(null);
    const [confirmationDialogAction, setConfirmationDialogAction] = React.useState(null);
    const { enqueueSnackbar } = useSnackbar();
    const { hasPerm } = React.useContext(ContextStore);
    const handleEdit = (ev) => {
        console.log('editing', props.data.id);
        history.push(`/trip/${props.data.id}`);
    };
    const doDelete = (ev) => {
        console.log('deleting', props.data.id);
        // props.onDelete && props.onDelete(props.data.id);
        disableMuiButton(deleteRef.current);
        props.setRelatedLoading(true);
        (async () => {
            try {
                const res = await dataAPI(DELETE, props.resource, { id: props.data.id });
                console.log('delete', res);
                if (res.statusCode === 204) {
                    enqueueSnackbar('Trip deleted', { variant: 'default' });
                    props.onDelete && props.onDelete(props.data.id);
                    history.push(`/trip`, { deleted: props.data.id });
                } else if (res.statusCode === 400) {
                    enqueueSnackbar('Error deleting trip', { variant: 'error' });
                }
            } catch (e) {
                console.log('error deleting trip', e);
                enqueueSnackbar('Error deleting trip', { variant: 'error' });
            } finally {
                if (deleteRef && deleteRef.current) {
                    enableMuiButton(deleteRef.current);
                }
                props.setRelatedLoading(false);
            }
        })();
    };

    const handleDelete = (ev) => {
        setConfirmationDialogAction('delete_trip');
    };

    const confirmationDialogCancel = () => {
        setConfirmationDialogAction(null);
    };
    const confirmationDialogConfirm = () => {
        console.log('confirm', confirmationDialogAction);
        switch (confirmationDialogAction) {
            case 'delete_trip':
                doDelete();
                break;
            default:
                break;
        }
        confirmationDialogCancel();
    };

    const now = moment.utc();

    const scheduleStr = props.data.schedule;
    const schedule = scheduleStr ? rrulestr(scheduleStr, { forceset: false }) : null;

    const trip_start_date = moment.utc(props.data.start_datetime);
    // const trip_length = Math.ceil(props.data.length / 86400) > 1 ? (Math.ceil(props.data.length / 86400) - 1) : 1;
    const trip_end_date = moment.utc(props.data.start_date).add(props.data.length, 'seconds');


    let nonBespokeStartDate = moment.utc().format('MMMM DD, YYYY');
    let nonBespokeEndDate = 'Active';

    if (!props.data.is_bespoke && props.data.schedule) {
        if (schedule?.options.dtstart) {
            nonBespokeStartDate = moment.utc(schedule?.options.dtstart)?.format('MMMM DD, YYYY');
        }

        if (props.data.schedule.split('UNTIL')[1]) {
            nonBespokeEndDate = props.data.schedule.split('UNTIL')[1];
            nonBespokeEndDate = nonBespokeEndDate.slice(1, 9);
            nonBespokeEndDate = moment.utc(nonBespokeEndDate).format('MMMM DD, YYYY');
        }
    }

    const formatTripPeriod = () => {
        if (props.data.is_bespoke) {
            const sdt_formatted = trip_start_date.tz(props.data.start_timezone).format('MMMM DD, YYYY');
            const edt_formatted = trip_end_date.tz(props.data.start_timezone).format('MMMM DD, YYYY');
            if (sdt_formatted === edt_formatted) {
                return `${sdt_formatted}`;
            }
            return `${sdt_formatted} ${EMDASH} ${edt_formatted}`;
        } else {

            return `${nonBespokeStartDate} ${EMDASH} ${nonBespokeEndDate}`;
        }
    };

    const tripPeriod = formatTripPeriod();
    const relativeStartTime = props.data.is_bespoke ? (now.isBefore(trip_start_date) ? `Starts ${trip_start_date.fromNow()} at ${trip_start_date.format('MMM D YYYY h:mm A Z')}` : `Started ${trip_start_date.fromNow()} at ${trip_start_date.format('MMM D YYYY h:mm A Z')}`) : null;

    return (
        <div className={classes.root}>
            {confirmationDialogAction ? (
                <ConfirmationDialog
                    classes={{ paper: classes.paper }}
                    keepMounted
                    onCancel={confirmationDialogCancel}
                    onConfirm={confirmationDialogConfirm}
                    title={ConfirmationDialogText[confirmationDialogAction].title}
                    okButtonText={ConfirmationDialogText[confirmationDialogAction].okButtonText}
                    cancelButtonText={ConfirmationDialogText[confirmationDialogAction].cancelButtonText}
                    open={!!confirmationDialogAction}
                >
                    {ConfirmationDialogText[confirmationDialogAction].body}
                </ConfirmationDialog>
            ) : null}
            <div className={classes.scrollableRoot}>
                <Grid container direction="row" mb={2}>
                    <Grid item xs={10}>
                        <Typography variant="h6">Trip Details</Typography>
                    </Grid>
                    <Grid item xs={2} container justifyContent="flex-end" alignItems="baseline">
                        <Tooltip title="Close">
                            <IconButton aria-label="close-preview" onClick={props.onClose} size="small" sx={{ color: 'rgba(0, 0, 0, 0.87)' }}>
                                <CloseIcon />
                            </IconButton>
                        </Tooltip>
                    </Grid>
                </Grid>
                {props.data.itinerary ? <PreviewImage image={props.data.cover_image} preset="cover_368x250" /> : null}
                {/* CP-8349: a guide group's dates are synthetic -- say what it is instead. */}
                {props.data.is_staff_chat_group ? (
                    <PreviewField label="Type" value="Staff-only guide group (no dates)" />
                ) : (
                    <PreviewField label="Dates" value={tripPeriod} subvalue={relativeStartTime} />
                )}
                <PreviewField label="Name" value={props.data.name} />
                {props.data.internal_name && <PreviewField label="Internal Name" value={props.data.internal_name} />}
                <PreviewField label="Description" value={props.data.description} />
                {props.data.is_bespoke && (
                    <PreviewField
                        label="User Map"
                        value={
                            <Link href={props.data.map_link} target="_blank">
                                Open in New Tab
                            </Link>
                        }
                    />
                )}
                <PreviewField label="External ID" value={props.data.custom_id} />
                <PreviewField label="Internal ID" value={props.data.id} />
            </div>

            <Box
                mt={1}
                style={{
                    position: 'sticky',
                }}
            >
                {/* CP-8427: bottom right, primary action on the right (Martina): [DELETE] [EDIT]. */}
                <FormActionButtons
                    size="medium"
                    onDelete={handleDelete}
                    deletePerm="tour.delete"
                    deleteRef={deleteRef}
                    onSubmit={hasPerm('tour', 'read') ? handleEdit : undefined}
                    submitRef={editRef}
                    submitLabel="Edit"
                    submitIcon={<EditIcon />}
                />
            </Box>
        </div>
    );
};

const TripPreview = (props) => {
    const { hasPerm } = React.useContext(ContextStore);
    const resource = 'tour';


    const handleOnLoad = (data) => {
        props.onLoad && props.onLoad(props.id, data);
    };
    if (!hasPerm(resource, 'read')) {
        return <AccessDeniedView />
    }
    return (
        <RecordDetails resource={resource} id={props.id} onLoad={handleOnLoad} fullResponse>
            <RecordDetailsContext.Consumer>
                {(res) => <TripPreviewPanel resource={resource} setRelatedLoading={props.setRelatedLoading} id={res.data.id} data={res.data} onClose={props.onClose} onDelete={props.onDelete} />}
            </RecordDetailsContext.Consumer>
        </RecordDetails>
    );
};

export default TripPreview;
